/**
 * @file Contexte du layout public piloté par la page affichée.
 * @module layouts.public.public-page-context
 *
 * Le layout public (Header + contenu + Footer) est rendu une seule fois par la
 * route layout ; les pages n'en composent plus les briques. Quand une page a un
 * besoin spécifique, elle le déclare elle-même via ces hooks plutôt que d'être
 * recensée dans le layout — même doctrine que `layouts/app/full-height-context` :
 * la couche partagée reste agnostique des routes de l'app consommatrice, et le
 * réglage vit dans le fichier de la page concernée (source unique).
 *
 * Deux besoins couverts :
 * - `usePublicPageHeader(props)` : la page d'accueil fournit au Header ses
 *   données de navigation (catégories, coups de cœur, callbacks de scroll).
 *   Les autres pages ne déclarent rien → header sans menu.
 * - `usePublicPageBackground(color)` : fond de page spécifique (certaines
 *   branches de la gestion de réservation).
 * - `usePublicPageBottomInset(height)` : espace ajouté sous le Footer quand la
 *   page affiche une barre sticky en bas d'écran (tunnel d'achat mobile), pour
 *   que le Footer reste entièrement lisible en fin de scroll.
 *
 * Le provider expose aussi la ref du Header (`usePublicHeaderRef`) pour les
 * pages qui doivent mesurer sa hauteur (offset de scroll sous header sticky).
 *
 * Comme pour full-height-context, l'API d'enregistrement est d'identité stable
 * et les hooks de déclaration s'enregistrent en `useLayoutEffect` (appliqué
 * avant le premier paint) avec libération au démontage, gardée pour ne pas
 * effacer la déclaration déjà posée par la page suivante lors d'une navigation.
 */
import PropTypes from 'prop-types';
import {createContext, useContext, useLayoutEffect, useMemo, useRef, useState} from 'react';

const PublicPageApiContext = createContext(null);
const PublicPageValueContext = createContext({headerProps: null, background: null, bottomInset: null, headerRef: null});

/**
 * Égalité de surface entre deux objets de props. Garde-fou contre une page qui
 * passerait un objet recréé à chaque rendu (non mémoïsé) à contenu identique :
 * sans cette comparaison, chaque re-register déclencherait un re-render du
 * layout, donc de la page, donc un nouveau register — boucle de rendu infinie.
 *
 * @param {Object|null} a - Objet de props courant
 * @param {Object|null} b - Objet de props candidat
 * @return {boolean} Vrai si les deux objets ont les mêmes clés et valeurs (===)
 */
const isSameShallow = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  return keysA.length === keysB.length && keysA.every((key) => a[key] === b[key]);
};

/**
 * Fournit l'état « que déclare la page courante au layout public ». Doit
 * envelopper à la fois le layout (lecteur) et les pages de son Outlet
 * (déclarantes).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @return {JSX.Element}
 */
export function PublicPageProvider({children}) {
  const [headerProps, setHeaderProps] = useState(null);
  const [background, setBackground] = useState(null);
  const [bottomInset, setBottomInset] = useState(null);
  const headerRef = useRef(null);

  // API d'identité stable ([] en deps) : les hooks de déclaration peuvent en
  // dépendre sans se ré-exécuter à chaque changement de valeur.
  const api = useMemo(() => ({
    // Conserve la référence courante si le contenu n'a pas changé (garde
    // anti-boucle) ; la libération utilise la même égalité pour rester
    // symétrique avec ce qui a réellement été conservé
    registerHeaderProps: (props) => setHeaderProps((current) => (isSameShallow(current, props) ? current : props)),
    unregisterHeaderProps: (props) => setHeaderProps((current) => (isSameShallow(current, props) ? null : current)),
    registerBackground: (color) => setBackground(color),
    unregisterBackground: (color) => setBackground((current) => (current === color ? null : current)),
    registerBottomInset: (height) => setBottomInset(height),
    unregisterBottomInset: (height) => setBottomInset((current) => (current === height ? null : current)),
  }), []);

  const value = useMemo(() => ({headerProps, background, bottomInset, headerRef}), [headerProps, background, bottomInset]);

  return (
    <PublicPageApiContext.Provider value={api}>
      <PublicPageValueContext.Provider value={value}>
        {children}
      </PublicPageValueContext.Provider>
    </PublicPageApiContext.Provider>
  );
}

PublicPageProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Lu par le layout : déclarations de la page courante (props de navigation du
 * Header, fond de page) et ref à poser sur le Header.
 *
 * @return {Object} `{headerProps, background, bottomInset, headerRef}`
 */
export function usePublicPageValues() {
  return useContext(PublicPageValueContext);
}

/**
 * Appelé par une page pour fournir au Header ses données de navigation
 * (catégories, coups de cœur, callbacks). L'objet DOIT être mémoïsé par la
 * page (`useMemo`), sinon l'enregistrement se rejoue à chaque rendu.
 *
 * @param {Object|null} props - Props additionnelles passées au Header (null tant
 *   que la page n'a rien à déclarer, ex. données pas encore chargées)
 * @return {void}
 */
export function usePublicPageHeader(props) {
  const api = useContext(PublicPageApiContext);

  useLayoutEffect(() => {
    if (!api || !props) return undefined;
    api.registerHeaderProps(props);
    return () => api.unregisterHeaderProps(props);
  }, [api, props]);
}

/**
 * Appelé par une page pour poser un fond de page spécifique sur le conteneur
 * du layout (à la place du fond papier par défaut).
 *
 * @param {string|null} color - Couleur CSS/MUI, ou null pour le fond par défaut
 * @return {void}
 */
export function usePublicPageBackground(color) {
  const api = useContext(PublicPageApiContext);

  useLayoutEffect(() => {
    if (!api || !color) return undefined;
    api.registerBackground(color);
    return () => api.unregisterBackground(color);
  }, [api, color]);
}

/**
 * Appelé par une page qui affiche une barre sticky en bas d'écran : ajoute un
 * espace de la hauteur donnée sous le Footer pour qu'il reste entièrement
 * lisible en fin de scroll.
 *
 * @param {string|null} height - Hauteur CSS (ex. `'calc(70px + env(safe-area-inset-bottom, 0px))'`), ou null
 * @return {void}
 */
export function usePublicPageBottomInset(height) {
  const api = useContext(PublicPageApiContext);

  useLayoutEffect(() => {
    if (!api || !height) return undefined;
    api.registerBottomInset(height);
    return () => api.unregisterBottomInset(height);
  }, [api, height]);
}

/**
 * Ref posée sur le Header par le layout — pour mesurer sa hauteur (offset de
 * scroll sous le header sticky).
 *
 * @return {Object|null} Ref React du Header
 */
export function usePublicHeaderRef() {
  return useContext(PublicPageValueContext).headerRef;
}
